import { FormType, GetInspectionFormsRequestDTO, RecordState } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const formKeys = {
  all: ['forms'] as const,
  list: (limit: number, state: RecordState | undefined) => [...formKeys.all, 'list', limit, state] as const,
  details: () => [...formKeys.all, 'detail'] as const,
  detail: (formId: string | null | undefined, isAWA: boolean) => [...formKeys.details(), formId, isAWA] as const,
  revisions: (formId: string | null | undefined) => [...formKeys.all, 'revisions', formId] as const,
};

export const inspectionFormKeys = {
  all: ['inspection-forms'] as const,
  byAsset: (assetId: string, formType: FormType, limit: number | undefined, isAWA: boolean) =>
    [...inspectionFormKeys.all, assetId, formType, limit, isAWA] as const,
  infinite: (filters?: GetInspectionFormsRequestDTO['filters'], orderBy?: GetInspectionFormsRequestDTO['orderBy']) =>
    [...inspectionFormKeys.all, 'infinite', filters, orderBy] as const,
};

export const getFormsQueryOptions = ({ limit = 100, state }: { limit?: number; state?: RecordState } = {}) => {
  return queryOptions({
    queryKey: formKeys.list(limit, state),
    queryFn: async () => (await (await ApiService.GetForms({ limit, filters: { state } })).json()).data,
  });
};

export const getFormByIdQueryOptions = ({ formId, isAWA = false }: { formId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: formKeys.detail(formId, isAWA),
    queryFn: async () => {
      if (!formId) return null;
      return await (await (isAWA ? AWAApiV2Service : ApiService).GetFormById(formId)).json();
    },
    enabled: !!formId,
  });
};

export const getFormRevisionsQueryOptions = ({ formId }: { formId: string | undefined }) => {
  return queryOptions({
    queryKey: formKeys.revisions(formId),
    queryFn: async () => {
      if (!formId) return [];
      return (await (await ApiService.GetFormRevisions(formId)).json()).revisions;
    },
    enabled: !!formId,
  });
};

export const getAssetFormsByTypeQueryOptions = ({
  assetId,
  formType,
  limit,
  isAWA = false,
}: {
  assetId: string;
  formType: FormType;
  limit?: number;
  isAWA?: boolean;
}) => {
  return queryOptions({
    queryKey: inspectionFormKeys.byAsset(assetId, formType, limit, isAWA),
    queryFn: async () =>
      (await (await (isAWA ? AWAApiV2Service : ApiService).GetAssetFormsByType({ type: formType, assetId, ...(limit ? { limit } : {}) })).json()).forms,
    enabled: !!assetId,
  });
};

export const getInspectionFormsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetInspectionFormsRequestDTO['filters'];
  orderBy?: GetInspectionFormsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: inspectionFormKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetInspectionForms({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};
